import React, { useEffect, useMemo, useState } from "react";
import { Excalidraw } from "../../../packages/excalidraw/index";
import { restoreElements } from "../../../packages/excalidraw/data/restore";
import { isInitializedImageElement } from "../../../packages/excalidraw/element/typeChecks";
import {
  ExcalidrawImperativeAPI,
  ExcalidrawInitialDataState,
} from "../../../packages/excalidraw/types";
import { ImportedDataState } from "../../../packages/excalidraw/data/types";
import { loadSceneImagesFromCloud } from "../../data/cloudScene";
import { fitLoadedScene } from "../../data/fitLoadedScene";
import { RenderIntegration } from "../canvas/renderers/RenderIntegration";

/**
 * The read-only canvas for a shared file. Renders the core editor with
 * `viewModeEnabled` and — critically — NO `onChange`/save handler, so there is
 * no write path from this surface (no PUT, no localStorage buffer). Pan/zoom
 * work; nothing else.
 *
 * Kept free of chrome so the next slice can reuse it verbatim in the chrome-less
 * iframe/embed mode.
 */
export const ShareScene: React.FC<{
  fileId: string;
  scene: ImportedDataState;
  /** Zoom-to-fit the loaded scene in both share presentations. */
  fitToContentOnLoad?: boolean;
  /**
   * Hide Excalidraw's own UI layer (menu, zoom controls, footer) entirely, so
   * an embedded mindmap is just the canvas. The standalone viewer keeps them.
   */
  chromeless?: boolean;
}> = ({ fileId, scene, fitToContentOnLoad, chromeless }) => {
  const [api, setApi] = useState<ExcalidrawImperativeAPI | null>(null);
  const [readyViewport, setReadyViewport] = useState<{
    apiId: string;
    initialData: ExcalidrawInitialDataState;
  } | null>(null);

  const initialData: ExcalidrawInitialDataState = useMemo(
    () => ({
      elements: restoreElements(scene.elements, null, {
        repairBindings: true,
      }),
      // Keep this partial. A full restore fills non-persisted viewport fields
      // with zeroes, which can race Excalidraw's real container measurement.
      appState: scene.appState ?? undefined,
    }),
    [scene],
  );

  // Best-effort load of image assets referenced by the scene. Reads only.
  useEffect(() => {
    if (!api) {
      return;
    }
    let cancelled = false;
    const fileIds = (initialData.elements ?? [])
      .filter(isInitializedImageElement)
      .map((el) => el.fileId);
    if (fileIds.length) {
      loadSceneImagesFromCloud(fileId, fileIds).then(({ loadedFiles }) => {
        if (!cancelled && loadedFiles.length) {
          api.addFiles(loadedFiles);
        }
      });
    }
    return () => {
      cancelled = true;
    };
  }, [api, fileId, initialData]);

  // The API can arrive before initialData. Wait for the loaded scene and a
  // measured canvas, and hide it until the fitted viewport has painted.
  useEffect(() => {
    if (!api || !fitToContentOnLoad) {
      return;
    }
    return fitLoadedScene(api, initialData.elements ?? [], true, () => {
      setReadyViewport({ apiId: api.id, initialData });
    });
  }, [api, fitToContentOnLoad, initialData]);

  const viewportReady =
    !fitToContentOnLoad ||
    (!!api &&
      readyViewport?.apiId === api.id &&
      readyViewport?.initialData === initialData);

  return (
    // In embed mode the whole Excalidraw UI layer is hidden (see index.scss).
    // `viewModeEnabled` only removes the *editing* tools — Excalidraw still
    // renders its own default menu, zoom controls and footer when no children
    // override them, so a mindmap framed in a Notion page was arriving with a
    // hamburger menu and two stacked badges. Canvas pan/zoom is a canvas
    // interaction and keeps working without that layer.
    <div
      className={chromeless ? "share-scene--chromeless" : undefined}
      style={{ width: "100%", height: "100%", position: "relative" }}
    >
      <div
        data-testid="share-scene-canvas"
        style={{
          width: "100%",
          height: "100%",
          visibility: viewportReady ? "visible" : "hidden",
        }}
      >
        <Excalidraw
          excalidrawAPI={setApi}
          initialData={initialData}
          // Integration elements (Notion cards, mindmap pills) are drawn by a DOM
          // overlay, and the core editor renders that overlay only when a renderer
          // is supplied. Without this a shared mindmap arrived as connector arrows
          // with no nodes. `viewModeEnabled` reaches the renderer through
          // App.tsx, which is what keeps the pills inert here.
          renderIntegration={RenderIntegration}
          viewModeEnabled
          UIOptions={{
            canvasActions: {
              // "Save to..." (JSON export) is an editor-only action — it makes
              // no sense on a read-only shared view. `viewModeEnabled` already
              // hides Open/Save/Reset-canvas (their actions gate on it), but the
              // JSON export item doesn't, so it's disabled explicitly here.
              export: false,
              // Exporting an image of the shared mindmap is still genuinely
              // useful for a viewer, so keep it.
              saveAsImage: true,
            },
          }}
        />
      </div>
      {!viewportReady && (
        <div
          data-testid="share-scene-loading"
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "var(--island-bg-color, #fff)",
            color: "#6b7280",
          }}
        >
          Loading…
        </div>
      )}
    </div>
  );
};

export default ShareScene;
